// Preference-driven layout tokens. Turns the Display & Layout preferences
// (density + side padding + the Theme card's screen-edge padding floor) into
// the concrete spacing values that PageShell and
// every authenticated page consume. These replace what used to be static
// module-level exports in components/PageShell, so density/padding changes flow
// through the whole app from a single source of truth.
//
// getLayoutTokens is a pure function (no React) so it can be unit-tested per
// density x side-padding combination without asserting emotion classes.
import { useMemo } from 'react';
import { Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';

import {
  DEFAULT_EDGE_PADDING_PX,
  LayoutDensity,
  MAX_EDGE_PADDING_PX,
  SidePadding,
  useLayoutPreferences,
} from '../contexts/LayoutPreferencesContext';

// Responsive spacing-multiplier maps (MUI theme.spacing units; base 8px).
type ResponsiveSpacing = { xs: number; sm?: number; md: number };

// The three content widths a page can ask for. Which pixel cap each maps to is
// itself a function of the side-padding preference — see PAGE_MAX_WIDTH.
export type PageWidth = 'narrow' | 'standard' | 'wide';

export interface LayoutTokens {
  // Horizontal side padding applied to the PageShell (driven by side padding).
  pageSidePaddingX: ResponsiveSpacing;
  // Max content width per PageWidth (driven by side padding). Under
  // edge-to-edge every entry is '100%' — an uncapped, genuinely full-bleed
  // page. This is the token that actually governs wasted horizontal space:
  // side padding alone only ever trimmed single-digit pixels, while the cap
  // was routinely stranding hundreds per side on a wide display.
  pageMaxWidth: Record<PageWidth, number | string>;
  // Vertical rhythm around the main content area (driven by density).
  appMainSx: SystemStyleObject<Theme>;
  // Bottom margin below a page header block.
  pageHeaderSx: SystemStyleObject<Theme>;
  // Spacing prop for top-level page <Grid container> layouts.
  pageGridSpacing: number;
  // Padding for Paper panels.
  panelPaddingSx: SystemStyleObject<Theme>;
  // Padding for CardContent.
  cardContentPaddingSx: SystemStyleObject<Theme>;
  // Vertical rhythm between stacked sections within a page. Without this token
  // every page hardcoded its own (mb:2 in Reports/HabitStats/TimeEntries, mb:3
  // in Help), which is why density barely moved those pages.
  sectionGapSx: SystemStyleObject<Theme>;
  // Padding for DialogContent. Dialogs portal to document.body so they cannot
  // inherit appMainSx, and there is no MuiDialog theme override — a token is
  // the only mechanism that lets density reach them at all.
  dialogContentPaddingSx: SystemStyleObject<Theme>;
  // Extra cell padding overrides for dense tables (empty in comfortable mode).
  tableCellPaddingSx: SystemStyleObject<Theme>;
}

const SIDE_PADDING_X: Record<SidePadding, ResponsiveSpacing> = {
  standard: { xs: 2, sm: 2.5, md: 3 },
  reduced: { xs: 1.5, sm: 2, md: 2 },
  // xs:1 (8px) is a deliberate safe minimum so mobile never overflows. From sm
  // up the preset itself is zero — the user-set screen-edge padding floor
  // (Theme card, default 8px) is what keeps titles off the bezel there, and
  // dialling it to 0 restores a genuinely zero gutter.
  edge: { xs: 1, sm: 0, md: 0 },
};

// One MUI spacing unit is 8px; the screen-edge floor is stored in px so the
// Theme card can speak in pixels while the tokens stay in spacing units.
const SPACING_UNIT_PX = 8;

const clampEdgeFloor = (px: number): number =>
  Number.isFinite(px) ? Math.min(MAX_EDGE_PADDING_PX, Math.max(0, px)) : DEFAULT_EDGE_PADDING_PX;

// Lift every breakpoint of a side-padding preset to at least the floor. The
// floor only ever widens a gutter — a preset already past it is untouched.
const floorSidePadding = (
  preset: ResponsiveSpacing,
  edgePaddingPx: number,
): ResponsiveSpacing => {
  const floorUnits = clampEdgeFloor(edgePaddingPx) / SPACING_UNIT_PX;
  const floored: ResponsiveSpacing = {
    xs: Math.max(preset.xs, floorUnits),
    md: Math.max(preset.md, floorUnits),
  };
  if (preset.sm !== undefined) floored.sm = Math.max(preset.sm, floorUnits);
  return floored;
};

// Content caps per side-padding setting. Standard keeps the historical values;
// reduced widens them ~25%; edge removes the cap entirely so the page fills
// the viewport. Before this token existed the caps were fixed constants, so
// selecting edge-to-edge on a 1616px display still left 88px stranded per side
// on a 'standard' page and 328px on a 'narrow' one.
const PAGE_MAX_WIDTH: Record<SidePadding, Record<PageWidth, number | string>> = {
  standard: { narrow: 960, standard: 1440, wide: 1760 },
  reduced: { narrow: 1200, standard: 1760, wide: 2200 },
  edge: { narrow: '100%', standard: '100%', wide: '100%' },
};

interface DensityTokens {
  mainPaddingY: ResponsiveSpacing;
  headerMargin: ResponsiveSpacing;
  gridSpacing: number;
  panelPadding: ResponsiveSpacing;
  cardPadding: ResponsiveSpacing;
  tableCell: SystemStyleObject<Theme>;
  sectionGap: ResponsiveSpacing;
  // Dialogs get separate axes. MUI's own DialogContent default is 20px/24px
  // (vertical/horizontal), so a single `p` token tuned for vertical rhythm
  // would silently narrow every dialog's side gutter — 24px to 16px on xs,
  // where dialogs are near full-width and can least afford it.
  dialogPaddingY: ResponsiveSpacing;
  dialogPaddingX: ResponsiveSpacing;
}

const DENSITY_TOKENS: Record<LayoutDensity, DensityTokens> = {
  comfortable: {
    mainPaddingY: { xs: 2, md: 2.5 },
    headerMargin: { xs: 2, md: 2.5 },
    gridSpacing: 2,
    panelPadding: { xs: 2, md: 2.5 },
    cardPadding: { xs: 2, md: 2 },
    tableCell: {},
    sectionGap: { xs: 2, md: 2.5 },
    // Matches MUI's stock 20px/24px at comfortable, so wiring a dialog into
    // the token layer is visually neutral until the user picks compact.
    dialogPaddingY: { xs: 2.5, md: 2.5 },
    dialogPaddingX: { xs: 3, md: 3 },
  },
  compact: {
    mainPaddingY: { xs: 1.5, md: 1.5 },
    headerMargin: { xs: 1.5, md: 1.5 },
    gridSpacing: 1.5,
    panelPadding: { xs: 1.5, md: 1.5 },
    cardPadding: { xs: 1.5, md: 1.5 },
    tableCell: { py: 0.5, px: 1 },
    sectionGap: { xs: 1.5, md: 1.5 },
    dialogPaddingY: { xs: 1.5, md: 1.5 },
    dialogPaddingX: { xs: 2, md: 2 },
  },
};

export const getLayoutTokens = (
  density: LayoutDensity,
  sidePadding: SidePadding,
  edgePaddingPx: number = DEFAULT_EDGE_PADDING_PX,
): LayoutTokens => {
  const d = DENSITY_TOKENS[density] ?? DENSITY_TOKENS.comfortable;
  const pageSidePaddingX = floorSidePadding(
    SIDE_PADDING_X[sidePadding] ?? SIDE_PADDING_X.standard,
    edgePaddingPx,
  );
  const pageMaxWidth = PAGE_MAX_WIDTH[sidePadding] ?? PAGE_MAX_WIDTH.standard;

  return {
    pageSidePaddingX,
    pageMaxWidth,
    appMainSx: { flexGrow: 1, py: d.mainPaddingY },
    pageHeaderSx: { mb: d.headerMargin },
    pageGridSpacing: d.gridSpacing,
    panelPaddingSx: { p: d.panelPadding },
    cardContentPaddingSx: {
      p: d.cardPadding,
      '&:last-child': { pb: d.cardPadding },
    },
    tableCellPaddingSx: d.tableCell,
    sectionGapSx: { mb: d.sectionGap },
    dialogContentPaddingSx: { py: d.dialogPaddingY, px: d.dialogPaddingX },
  };
};

export const useLayoutTokens = (): LayoutTokens => {
  const { layoutDensity, sidePadding, edgePaddingPx } = useLayoutPreferences();
  return useMemo(
    () => getLayoutTokens(layoutDensity, sidePadding, edgePaddingPx),
    [layoutDensity, sidePadding, edgePaddingPx],
  );
};

export default useLayoutTokens;
